import * as React from 'react'
import produce from 'immer'
import { Base64 } from 'js-base64'
import * as CSV from 'csv-string'
import { Button, Form, Input, Spin, Dropdown, Menu, message } from 'antd'
import { cloneDeep, get, isNil, set, isEqual, isEmpty, flow } from 'lodash'
import { HARD_SPACE } from 'helpers/utils'
import { ValidationError, showError, showClientNotifications } from 'helpers/errors'
import {
  createFieldFactory,
  createHandleSubmit,
  createLabelFactory,
  getChangedItems,
  isReadOnly,
} from 'helpers/formViews'
import { formatUserTime } from 'helpers/dateTime'
import { t } from 'helpers/i18n'
import { withRouter } from 'react-router-dom'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_CHANGE_EVENT, LIST_MODAL_CLEAR_SELECTION, FORM_SUBMIT_EVENT } from 'options/events'
import FormButtons from 'elements/FormButtons'
import Modal from 'elements/Modal'
import Icon from 'elements/Icon'
import SelectInventory from 'containers/Inventory/Select'
import SelectListItems from 'containers/Lists/Items/Select'
import Drawer from 'elements/Drawer'
import FileUpload from 'elements/FileUpload'
import SelectItems from 'containers/IssueLimitLists/Items/Select'
import SelectIssueLimitList from 'containers/IssueLimitLists/Select'
import { Row, Col } from 'elements/Grid'
import Checkbox from 'elements/Checkbox'
import CreateIssueLimitList from 'containers/IssueLimitLists/Create'
import IssueLimitListItems from 'containers/IssueLimitLists/Items'
import Operators from 'containers/IssueLimitLists/Operators'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()

    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  handleFormChange = () => this.setState({ formHasChanged: true })

  handleCreateIssueLimitList = async (saved) => {
    await this.promiseState({ item: saved, createIssueLimitListVisible: false })
    await this.fetchItem()
    await this.props.onSave?.()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.issueLimitListId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    if (isNil(itemId) && !this.props.linkTargetRecord) {
      this.setState({ createIssueLimitListVisible: true })
    } else {
      try {
        const responses = await Promise.all([
          this.props.getItem(itemId).catch((error) => {
            this.props.onCancel?.()
            throw error
          }),
          this.props.getSettings({ type: this.props.settingsType }),
        ])

        const item = cloneDeep(responses[0].value.data)

        const [issueLimitListItems] = await Promise.all([
          item.id ? this.props.getIssueLimitListItems(item.id) : Promise.resolve(null),
        ])

        this.setState({
          item,
          itemOriginal: cloneDeep(item),
          fieldSettings: get(responses[1], 'value.data.fieldSettings', []),
          locations: get(responses[2], 'value.data.items', []),
          issueLimitListItems: get(issueLimitListItems, 'value.data.items', []),
          issueLimitListItemsOriginal: get(issueLimitListItems, 'value.data.items', []),
          formHasChanged: false,
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  hasUnsavedChanges = () =>
    this.state.formHasChanged ||
    !this.state.item?.id ||
    !isEqual(this.state.item, this.state.itemOriginal) ||
    !isEqual(this.state.issueLimitListItems, this.state.issueLimitListItemsOriginal)

  handleActionsMenuClick = ({ key }) => {
    if (this.hasUnsavedChanges()) {
      message.error(t('saveChangesFirst'))
      return
    }

    switch (key) {
      case 'copyFromExistingRecord':
        Modal.confirm({
          autoFocusButton: 'ok',
          title: t('confirmCopyIssueLimitList'),
          okType: 'danger',
          okText: t('yes'),
          cancelText: t('no'),
          onOk: () => this.setState({ copyFromExistingVisible: true, copyFromExistingSelectedRecord: null }),
        })
        break

      case 'operatorsUsingIssueLimitList':
        this.setState({ operatorsVisible: true })
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await this.props.updateItem(item) : await this.props.createItem(item)

    await Promise.all([this.saveIssueLimitListItems(saved.value.data.id)])

    return saved
  }

  saveIssueLimitListItems = async (issueLimitListId) => {
    const { creating, updating, deleting } = getChangedItems(
      this.state.issueLimitListItemsOriginal,
      this.state.issueLimitListItems
    )

    if (!isEmpty(deleting)) {
      const response = await this.props.deleteIssueLimitListItems(
        issueLimitListId,
        deleting.map((each) => each.id)
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }

    if (!isEmpty(updating)) {
      const response = await this.props.updateIssueLimitListItems(
        issueLimitListId,
        updating.map((each) => ({ ...each, issueLimitListId }))
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }

    if (!isEmpty(creating)) {
      const response = await this.props.createIssueLimitListItems(
        issueLimitListId,
        creating.map(({ id, jobLabourId, ...rest }) => ({
          ...rest,
          issueLimitListId,
        }))
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }
  }

  handleItemsTableExtraButtonsClick = ({ key }) => {
    switch (key) {
      case 'addFromInventory':
        this.setState({ addFromInventoryVisible: true, addFromInventoryItems: [] })
        break

      case 'addFromList':
        this.setState({
          addFromListVisible: true,
          addFromListItems: [],
          selectPopulatedItems: [],
          selectPopulatedItemsData: [],
        })
        break

      case 'addFromFile':
        this.setState({
          addFromFileUploadVisible: true,
          selectPopulatedItems: [],
          selectPopulatedItemsData: [],
        })
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  handleAddFromInventorySubmit = async () => {
    try {
      const seed = Date.now() * -1

      this.setState({ addFromInventoryLoading: true })

      const response = await this.props.populateIssueLimitListItems(
        this.state.item.id,
        this.state.addFromInventoryItems.map((each) => each.barcode)
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      this.setState(
        produce((draft) => {
          get(response, 'value.data.items', []).forEach((each, index) => {
            if (isNil(draft.issueLimitListItems.find((one) => one.barcode === each.barcode))) {
              draft.issueLimitListItems.push({ ...each, id: seed - index, issueLimitListId: draft.item.id })
            }
          })

          draft.item.numberOfItems = draft.issueLimitListItems.length

          draft.addFromInventoryItems = []
          draft.addFromInventoryVisible = false
        })
      )

      Emitter.emit(LIST_MODAL_CLEAR_SELECTION)
    } catch (error) {
      showError({ error })
    } finally {
      this.setState({ addFromInventoryLoading: false })
    }
  }

  handleAddFromListSubmit = async () => {
    try {
      const seed = Date.now() * -1

      this.setState({ addFromListLoading: true })

      const response = await this.props.populateIssueLimitListItems(
        this.state.item.id,
        this.state.addFromListItems.map((each) => each.barcode)
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      const populatedItems = get(response, 'value.data.items', []).map((each, index) => ({
        ...each,
        id: each.id > 0 ? seed + index : (seed + index) * -1,
        issueLimitListId: this.state.item.id,
      }))

      this.setState({
        selectPopulatedItemsData: populatedItems,
        selectPopulatedItemsVisible: true,
        selectPopulatedItemsTitle: t('addFromList'),
      })

      Emitter.emit(LIST_MODAL_CLEAR_SELECTION)
    } catch (error) {
      showError({ error })

      this.setState({
        selectPopulatedItemsVisible: false,
        selectPopulatedItems: [],
      })
    } finally {
      this.setState({ addFromListLoading: false })
    }
  }

  handleAddFromFileUpload = async (fileList = []) => {
    try {
      const barcodes = CSV.parse(Base64.decode(fileList[0].fileContents)).map((parts) => parts[0])

      const response = await this.props.populateIssueLimitListItems(this.state.item.id, barcodes)

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      const seed = Date.now()

      const populatedItems = get(response, 'value.data.items', []).map((each, index) => ({
        ...each,
        id: each.id > 0 ? seed + index : (seed + index) * -1,
        issueLimitListId: this.state.item.id,
      }))

      this.setState({
        addFromFileUploadVisible: false,
        selectPopulatedItemsData: populatedItems,
        selectPopulatedItemsVisible: true,
        selectPopulatedItemsTitle: t('addFromFile'),
      })
    } catch (error) {
      showError({ error })

      this.setState({
        selectPopulatedItemsVisible: false,
        selectPopulatedItems: [],
      })
    }
  }

  handleSelectPopulatedItemsSubmit = () => {
    let seed = Date.now() * -1
    let count = 0

    this.setState(
      produce((draft) => {
        draft.selectPopulatedItems.forEach((each) => {
          if (isNil(draft.issueLimitListItems.find((one) => one.barcode === each.barcode))) {
            draft.issueLimitListItems.push({ ...each, id: seed--, issueLimitListId: draft.item.id })
            count++
          }
        })

        draft.item.numberOfItems = draft.issueLimitListItems.length
      }),
      () => message.success(`${count} ${t('itemsAdded')}`)
    )
  }

  handleCopyFromExisting = async () => {
    try {
      this.setState({ copyFromExistingLoading: true })

      const response = await this.props.getIssueLimitListItems(this.state.copyFromExistingSelectedRecord.id)
      const seed = Date.now() * -1

      this.setState((draft) => {
        draft.issueLimitListItems = response.value.data.items.map((each, index) => ({
          ...each,
          issueLimitListId: draft.item.id,
          id: seed - index,
        }))
        draft.item.numberOfItems = draft.issueLimitListItems.length
        draft.copyFromExistingVisible = false
      })
    } catch (error) {
      showError({ error })
    } finally {
      this.setState({ copyFromExistingLoading: false })
    }
  }

  refetchItem = () => {
    this.fetchItem()
    this.props.onSave?.()
  }

  render() {
    const { item, fieldSettings, validationErrors } = this.state

    if (!item || !fieldSettings) {
      return (
        <>
          <Spin />
          <Modal
            title={t('createIssueLimitList')}
            visible={this.state.createIssueLimitListVisible}
            onCancel={this.props.onCancel}
            okText={t('create')}
            onOk={() => Emitter.emit(FORM_SUBMIT_EVENT)}
            okButtonProps={{ loading: this.state.createIssueLimitListLoading }}
          >
            <CreateIssueLimitList
              onSave={(pending) => this.setState({ createIssueLimitListLoading: pending })}
              onSaveAndClose={this.handleCreateIssueLimitList}
              onCancel={this.props.onCancel}
            />
          </Modal>
        </>
      )
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          <Dropdown
            overlay={
              <Menu onClick={this.handleActionsMenuClick}>
                <Menu.Item key="copyFromExistingRecord" disabled={readOnly}>
                  {t('copyFromExistingIssueLimitList')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item key="operatorsUsingIssueLimitList">
                  {t('operatorsUsingIssueLimitList...')}
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button className="form-items-actions">
              {t('actions')}
              <Icon type="KeyboardArrowDown" />
            </Button>
          </Dropdown>
          <div className="form-items-container">
            <ValidationError errors={validationErrors} />
            <Row>
              <Col sm={24} md={8} xl={6}>
                <Form.Item label={createLabel('name')}>
                  {createFieldDecorator('name')(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              <Col sm={24} md={8} xl={6}>
                <Form.Item label={t('created')}>
                  <Input value={formatUserTime(item.createdDate, item.createdBy)} disabled />
                </Form.Item>
              </Col>
              <Col sm={24} md={8} xl={6}>
                <Form.Item label={HARD_SPACE}>
                  {createFieldDecorator('active', { valuePropName: 'checked' })(
                    <Checkbox readOnly={readOnly}>{createLabel('active')}</Checkbox>
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col sm={24} md={8} xl={6}>
                <Form.Item label={createLabel('locationName')}>
                  <Input value={item.locationName} disabled />
                </Form.Item>
              </Col>
              <Col sm={24} md={8} xl={6}>
                <Row>
                  <Col half>
                    <Form.Item label={createLabel('numberOfItems')}>
                      <Input value={item.numberOfItems} disabled />
                    </Form.Item>
                  </Col>
                  <Col half>
                    <Form.Item label={createLabel('numberOfOperators')}>
                      <Input value={item.numberOfOperators} disabled />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
            <IssueLimitListItems
              parentRecord={item}
              items={this.state.issueLimitListItems ?? []}
              originals={this.state.issueLimitListItemsOriginal ?? []}
              onChange={(values) => this.setState({ issueLimitListItems: values })}
              readOnly={readOnly}
              onTableExtraButtonsClick={this.handleItemsTableExtraButtonsClick}
            />
          </div>
          <div className="form-buttons-container">
            <FormButtons
              readOnly={readOnly}
              onCancel={this.props.onCancel}
              onSubmit={this.handleSubmit}
              saveButtonLoading={this.state.saveButtonLoading}
              saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
            />
          </div>
        </Form>
        <Modal
          title={t('addFromInventory')}
          visible={this.state.addFromInventoryVisible}
          onCancel={() => this.setState({ addFromInventoryVisible: false })}
          footer={
            <>
              <Button onClick={() => this.setState({ addFromInventoryVisible: false })}>{t('close')}</Button>
              <Button
                onClick={this.handleAddFromInventorySubmit}
                loading={this.state.addFromInventoryLoading}
                type="primary"
                disabled={isEmpty(this.state.addFromInventoryItems)}
              >
                {t('addToList')}
              </Button>
            </>
          }
          width={992}
        >
          <SelectInventory
            initialFilterDto={{
              locationIds: [this.state.item.locationId],
            }}
            onSelect={(values) => this.setState({ addFromInventoryItems: values })}
            mode="multiple"
            filterProps={{ disabled: true }}
          />
        </Modal>
        <Modal
          title={`${t('addFromList')} - ${item.locationName}`}
          visible={this.state.addFromListVisible}
          onCancel={() => this.setState({ addFromListVisible: false })}
          footer={
            <>
              <Button onClick={() => this.setState({ addFromListVisible: false })}>{t('close')}</Button>
              <Button
                onClick={this.handleAddFromListSubmit}
                loading={this.state.addFromListLoading}
                type="primary"
                disabled={isEmpty(this.state.addFromListItems)}
              >
                {t('select')}
              </Button>
            </>
          }
          width={992}
        >
          <SelectListItems
            onSelect={(values) => this.setState({ addFromListItems: values })}
            settingsType="issueLimitInventoryList"
            mode="multiple"
            filterProps={{
              locationId: item.locationId,
              entityType: 'Procurement',
              listType: 'Product',
              listItemMode: 'IssueLimitInventoryList',
            }}
          />
        </Modal>
        <Drawer
          title={t('addFromFile')}
          size="xs"
          visible={this.state.addFromFileUploadVisible}
          onClose={() => this.setState({ addFromFileUploadVisible: false })}
        >
          <FileUpload
            accept=".csv,text/csv"
            onUpload={this.handleAddFromFileUpload}
            onCancel={() => this.setState({ addFromFileUploadVisible: false })}
            extra={{
              type: 'info',
              message: t('fileRequirements'),
              description: t('issueLimitListUploadInfo'),
            }}
          />
        </Drawer>
        <Modal
          title={this.state.selectPopulatedItemsTitle}
          visible={this.state.selectPopulatedItemsVisible}
          onCancel={() => this.setState({ selectPopulatedItemsVisible: false })}
          footer={
            <>
              <Button onClick={() => this.setState({ selectPopulatedItemsVisible: false })}>
                {t('close')}
              </Button>
              <Button
                onClick={this.handleSelectPopulatedItemsSubmit}
                type="primary"
                disabled={isEmpty(this.state.selectPopulatedItems)}
              >
                {t('addToList')}
              </Button>
            </>
          }
          width={992}
        >
          {isNil(this.state.selectPopulatedItemsData) ? (
            <Spin />
          ) : (
            <SelectItems
              items={this.state.selectPopulatedItemsData ?? []}
              onSelect={(values) => this.setState({ selectPopulatedItems: values })}
              settingsType="issueLimitListFileItemList"
              mode="multiple"
            />
          )}
        </Modal>
        <Modal
          title={t('operatorsUsingLimitList')}
          visible={this.state.operatorsVisible}
          onCancel={() => this.setState({ operatorsVisible: false })}
          footer={<Button onClick={() => this.setState({ operatorsVisible: false })}>{t('close')}</Button>}
          width={992}
        >
          <Operators
            filterProps={{
              issueLimitListId: item.id,
              issueLimitListName: item.name,
              locationName: item.locationName,
            }}
            onCreate={this.refetchItem}
            onDelete={this.refetchItem}
            mode="multiple"
            readOnly={readOnly}
            allowDelete
          />
        </Modal>
        <Modal
          title={t('selectIssueLimitList')}
          visible={this.state.copyFromExistingVisible}
          okText={t('copy')}
          onOk={this.handleCopyFromExisting}
          okButtonProps={{
            loading: this.state.copyFromExistingLoading,
            disabled: !this.state.copyFromExistingSelectedRecord,
          }}
          onCancel={() =>
            this.setState({
              copyFromExistingVisible: false,
              copyFromExistingSelectedRecord: null,
            })
          }
          width={992}
        >
          <SelectIssueLimitList
            initialFilterDto={{
              locationIds: [this.state.item.locationId],
            }}
            onSelect={(values) => this.setState({ copyFromExistingSelectedRecord: values[0] })}
            filterProps={{ allowSelectLocations: false }}
          />
        </Modal>
      </>
    )
  }
}

export default flow(
  withRouter,
  Form.create({
    onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
  })
)(FormView)
